import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Users, BusFront } from "lucide-react";
import { Reveal } from "./Reveal";
import { GroupQuoteModal } from "../booking/GroupQuoteModal";
import { useBooking } from "../../context/BookingContext";
import { useSiteData } from "../../context/SiteData";
import { optImg } from "../../lib/img";

const CARDS = [
  {
    key: "private",
    chip: "1–7 People",
    chipIcon: Users,
    title: "Private Tours & Transfers",
    copy: "Your own Mercedes V-Class with a local driver-guide — door to door, at your pace.",
    cta: "Book Private Tour",
    image:
      "https://images.unsplash.com/photo-1688619102600-976b3bb41aaf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBzcHJpbnRlciUyMHZhbnxlbnwwfHx8fDE3OTA3NjQ5NzJ8MA&ixlib=rb-4.1.0&q=85",
    imageAlt: "Black luxury Mercedes V-Class van on a snow-covered Swiss alpine road — private tours and transfers for 1 to 7 people",
  },
  {
    key: "group",
    chip: "7–200 People",
    chipIcon: BusFront,
    title: "Group Tours & Transfers",
    copy: "Modern luxury coaches for clubs, companies and events — custom quotes within 24 hours.",
    cta: "Request Group Quote",
    image:
      "https://images.unsplash.com/photo-1759882608768-168d4c3a91c2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb2FjaCUyMGJ1c3xlbnwwfHx8fDE3OTA3NjQ5NzN8MA&ixlib=rb-4.1.0&q=85",
    imageAlt: "Modern luxury tour coach on a scenic Swiss mountain highway — group tours and transfers for 7 to 200 people",
  },
];

export const GroupBooking = () => {
  const { openBooking } = useBooking();
  const { fleet } = useSiteData();
  const [quoteOpen, setQuoteOpen] = useState(false);

  const handleClick = (key) => {
    if (key === "private") {
      openBooking({ mode: "tour" });
    } else {
      setQuoteOpen(true);
    }
  };

  return (
    <section className="relative bg-white py-16 sm:py-20" data-testid="group-booking-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center max-w-2xl mx-auto">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-[var(--brand)]">
            Travel in Any Group Size
          </p>
          <h2 className="mt-3 font-display font-bold tracking-tight text-3xl sm:text-4xl text-[#0b132b]" data-testid="group-booking-title">
            How Many of You Are Coming?
          </h2>
        </Reveal>

        <div className="mt-10 grid md:grid-cols-2 gap-6">
          {CARDS.map((c, i) => {
            const ChipIcon = c.chipIcon;
            return (
              <Reveal key={c.key} delay={i * 0.1}>
                <motion.button
                  type="button"
                  data-testid={`group-booking-${c.key}-card`}
                  onClick={() => handleClick(c.key)}
                  whileHover={{ y: -6 }}
                  transition={{ type: "spring", stiffness: 260, damping: 22 }}
                  className="group relative block w-full h-[340px] sm:h-[400px] rounded-3xl overflow-hidden text-start shadow-[0_24px_60px_-24px_rgba(11,19,43,0.35)] focus:outline-none focus:ring-4 focus:ring-[var(--brand)]/40"
                >
                  <img
                    src={optImg(c.image, 800)}
                    alt={c.imageAlt}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0b132b]/90 via-[#0b132b]/35 to-transparent" />

                  <div className="absolute top-5 left-5">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/90 backdrop-blur px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wide text-[var(--brand)]">
                      <ChipIcon className="w-3.5 h-3.5" /> {c.chip}
                    </span>
                  </div>

                  <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
                    <h3 className="font-display font-bold text-2xl sm:text-3xl text-white leading-tight">
                      {c.title} <span className="text-[var(--brand)]">—</span>{" "}
                      <span className="text-white/80">{c.chip}</span>
                    </h3>
                    <p className="mt-2 text-sm text-slate-300 max-w-md">{c.copy}</p>
                    <span
                      data-testid={`group-booking-${c.key}-button`}
                      className="mt-4 inline-flex items-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-6 py-3 shadow-lg shadow-[var(--brand)]/40 transition-all duration-300 group-hover:bg-[var(--brand-dark)] group-hover:gap-3"
                    >
                      {c.cta} <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </motion.button>
              </Reveal>
            );
          })}
        </div>
        {/* Fleet strip */}
        <Reveal delay={0.15} className="mt-12">
          <p className="text-center text-xs sm:text-sm font-semibold uppercase tracking-wider text-[var(--brand)]" data-testid="fleet-eyebrow">
            Our Fleet
          </p>
          <div className="mt-6 grid sm:grid-cols-3 gap-5" data-testid="fleet-grid">
            {fleet.map((v, i) => (
              <motion.div
                key={v.id}
                data-testid={`fleet-card-${i}`}
                whileHover={{ y: -5 }}
                transition={{ type: "spring", stiffness: 260, damping: 22 }}
                className="group rounded-3xl bg-[#f8fafc] border border-slate-100 overflow-hidden hover:bg-white hover:shadow-[0_20px_45px_-20px_rgba(11,19,43,0.25)] hover:border-[var(--brand)]/25 transition-colors duration-300"
              >
                <Link to={`/fleet/${v.slug || v.id}`} data-testid={`fleet-card-link-${i}`} aria-label={`View details of ${v.name}`}>
                  <div className="h-40 overflow-hidden">
                    <img
                      src={optImg(v.image, 800)}
                      alt={`${v.name} — ${v.capacity}, Voyage Switzerland fleet`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                </Link>
                <div className="p-5">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-display font-bold text-base text-[#0b132b]">{v.name}</h3>
                    <span className="shrink-0 rounded-full bg-[var(--brand-soft)] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-[var(--brand)]">
                      {v.capacity}
                    </span>
                  </div>
                  <p className="mt-1.5 text-xs text-slate-500 leading-relaxed">{v.copy}</p>
                  <div className="mt-3 flex items-center justify-end">
                    <Link
                      to={`/fleet/${v.slug || v.id}`}
                      data-testid={`fleet-details-link-${i}`}
                      className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-[var(--brand)] hover:gap-2 transition-all"
                    >
                      Details <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </Reveal>
      </div>
      <GroupQuoteModal open={quoteOpen} onClose={() => setQuoteOpen(false)} />
    </section>
  );
};
